import React, { createContext, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ThemeProvider, CssBaseline, Theme } from '@mui/material';
import {
  reefModernismThemeDark,
  reefModernismThemeLight,
  studioThemeDark,
  studioThemeLight,
  smartaiThemeDark,
  smartaiThemeLight,
  constellationsThemeDark,
  constellationsThemeLight,
  reefColors,
} from '../themes';
import { DEFAULT_PREFS, loadPrefs, savePrefs } from '../themes/themePrefs';
import type { ColorMode, ThemeId } from '../themes/themeTypes';

// ============================================================================
// Types
// ============================================================================

export type { ColorMode, ThemeId };

export interface ThemeDefinition {
  id: ThemeId;
  name: string;
  description: string;
  previewColors: [string, string, string]; // 3 swatch colors for preview
}

export interface ThemeContextValue {
  themeId: ThemeId;
  colorMode: ColorMode;
  setThemeId: (id: ThemeId) => void;
  setColorMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
  availableThemes: ThemeDefinition[];
}

// ============================================================================
// Constants
// ============================================================================

// architectureDesign.md §6 ranks `reef` (Reef Modernism) first: it is the Coral
// Creator brand identity and the fresh-visitor default, in light — its reference
// mode. `smartai` is the previous default and stays fully selectable, `studio`
// rides as the hub-style alternative, and `constellations` stays available as
// the inherited Image Lab look so existing users can keep their preference. A
// preference stored under the current PREFS_EPOCH outranks all of this; one
// saved before the rebrand does not (see themePrefs.loadPrefs).
//
// This array's order is the order both pickers render, so ranking changes here
// and nowhere else.
const AVAILABLE_THEMES: ThemeDefinition[] = [
  {
    id: 'reef',
    name: 'Reef Modernism',
    description: 'Terracotta on warm stone (default)',
    // Read from the tokens, so retuning a pigment moves the swatch with it.
    previewColors: [reefColors.terracotta, reefColors.ink, reefColors.stone],
  },
  {
    id: 'smartai',
    name: 'SmartAI Africa',
    description: 'Blue & cyan gradient',
    previewColors: ['#046bd2', '#354dc7', '#02f9fd'],
  },
  {
    id: 'studio',
    name: 'Studio Classic',
    description: 'Coral red & teal (hub-style surfaces)',
    previewColors: ['#E16565', '#2B9EB3', '#4DD0B8'],
  },
  {
    id: 'constellations',
    name: 'Constellations',
    description: 'Cyan-to-purple network nodes',
    previewColors: ['#07E3F7', '#1D9EE1', '#4029BD'],
  },
];

// Theme registry: maps themeId + colorMode to MUI Theme
const themeRegistry: Record<ThemeId, Record<ColorMode, Theme>> = {
  reef: {
    dark: reefModernismThemeDark,
    light: reefModernismThemeLight,
  },
  studio: {
    dark: studioThemeDark,
    light: studioThemeLight,
  },
  smartai: {
    dark: smartaiThemeDark,
    light: smartaiThemeLight,
  },
  constellations: {
    dark: constellationsThemeDark,
    light: constellationsThemeLight,
  },
};

// ============================================================================
// Context
// ============================================================================

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

interface ThemeManagerProviderProps {
  children: React.ReactNode;
  /**
   * Optional seed when no persisted prefs exist (e.g. F10 will pass
   * `defaultThemeForSurface('labs' | 'hub')` based on the active route).
   * Current-epoch stored prefs win — this only changes the *fallback* default.
   */
  initialThemeId?: ThemeId;
}

export const ThemeManagerProvider: React.FC<ThemeManagerProviderProps> = ({
  children,
  initialThemeId,
}) => {
  const seeded = useMemo(
    () => loadPrefs(initialThemeId ? { ...DEFAULT_PREFS, themeId: initialThemeId } : DEFAULT_PREFS),
    [initialThemeId],
  );
  const [themeId, setThemeIdState] = useState<ThemeId>(seeded.themeId);
  const [colorMode, setColorModeState] = useState<ColorMode>(seeded.colorMode);

  // Persist on change — but never on mount.
  //
  // The stored epoch means "chosen after the rebrand" (themePrefs.PREFS_EPOCH),
  // so only a real choice may write it. Persisting the seeded state on mount
  // would stamp whatever we just loaded, laundering a pre-rebrand preference
  // into a post-rebrand one and pinning it forever. That is not hypothetical:
  // an open tab hot-reloading a new epoch does exactly this.
  const hydrated = useRef(false);
  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true;
      return;
    }
    savePrefs({ themeId, colorMode });
  }, [themeId, colorMode]);

  const setThemeId = useCallback((id: ThemeId) => {
    setThemeIdState(id);
  }, []);

  const setColorMode = useCallback((mode: ColorMode) => {
    setColorModeState(mode);
  }, []);

  const toggleColorMode = useCallback(() => {
    setColorModeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const muiTheme = useMemo(() => themeRegistry[themeId][colorMode], [themeId, colorMode]);

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      themeId,
      colorMode,
      setThemeId,
      setColorMode,
      toggleColorMode,
      availableThemes: AVAILABLE_THEMES,
    }),
    [themeId, colorMode, setThemeId, setColorMode, toggleColorMode],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
